import socketService from '../services/socketService';
import { setMapaFaseFromSocket } from './faseActions';
import { shouldAcceptMenorSnapshotFull, fasesDoGrupo } from './mapaFaseUtils';

let wired = false;
let routeKeeperTimer = null;
let hashListenerBound = false;
let lastConfigHadMapaSocket = false;
/** Só subscribe/polling mapa enquanto a leitura ao vivo está ligada. */
let mapaFaseLiveAtivo = false;
/** Grupos que o mapa está acompanhando (1 normalmente, N em "todos os grupos"). */
let gruposAtivos = [];

const ROUTE_KEEPALIVE_MS = 3000;

function getClientConfigFromStore() {
  const store = window.store;
  if (!store) return null;
  const auth = store.getState().auth;
  return auth && auth.config;
}

function currentPathFromWindow() {
  if (typeof window === 'undefined') return '/';
  const hash = window.location.hash || '';
  if (hash.startsWith('#')) {
    const path = hash.slice(1) || '/';
    return path.startsWith('/') ? path : `/${path}`;
  }
  return window.location.pathname || '/';
}

function normalizePath(pathname) {
  if (!pathname) return '/';
  const base = pathname.split('?')[0];
  if (base.length > 1 && base.endsWith('/')) {
    return base.slice(0, -1);
  }
  return base;
}

function isMapaPath(pathname) {
  return normalizePath(pathname) === '/mapadefases';
}

function stopMapaRouteKeeper() {
  if (routeKeeperTimer) {
    clearInterval(routeKeeperTimer);
    routeKeeperTimer = null;
  }
}

function tickMapaLive() {
  if (!mapaFaseLiveAtivo) return;

  const cfg = getClientConfigFromStore();
  if (!cfg || !cfg.mapa_fase_socket) return;

  socketService.connect();

  if (gruposAtivos.length) {
    socketService.forceAuthAndSubscribe(gruposAtivos);
  }
}

function ensureMapaLiveKeeper() {
  if (!mapaFaseLiveAtivo || !isMapaPath(currentPathFromWindow())) {
    stopMapaRouteKeeper();
    return;
  }

  tickMapaLive();

  if (!routeKeeperTimer) {
    routeKeeperTimer = setInterval(tickMapaLive, ROUTE_KEEPALIVE_MS);
  }
}

function handleUpdate(payload) {
  if (!mapaFaseLiveAtivo) return;
  if (!payload || !Array.isArray(payload.data)) return;

  const grupoId = parseInt(payload.grupoId, 10);
  if (!gruposAtivos.includes(grupoId)) return;

  const store = window.store;
  if (!store) return;

  const state = store.getState();
  const listMap = (state.faseCad && state.faseCad.listMap) || [];
  // Em "todos os grupos" o snapshot só fala do próprio grupo — comparar com a
  // lista inteira faria todo full parecer "menor" e ser descartado.
  const base = gruposAtivos.length > 1 ? fasesDoGrupo(listMap, grupoId) : listMap;
  const isFull = payload.full !== false;

  if (!isFull && base.length === 0) return;
  if (
    isFull &&
    !shouldAcceptMenorSnapshotFull(base, payload.data, payload.authoritative === true)
  ) {
    return;
  }

  store.dispatch(
    setMapaFaseFromSocket({
      data: payload.data,
      full: isFull,
      controladorId: payload.controladorId,
      grupoId: payload.grupoId,
      authoritative: payload.authoritative === true,
    }),
  );
}

function handleSubscribed(payload) {
  if (!mapaFaseLiveAtivo) return;
  if (!payload || !payload.ok) return;
  if (!gruposAtivos.includes(parseInt(payload.grupoId, 10))) return;
  if (typeof window.__mapaFaseSubscribed === 'function') {
    window.__mapaFaseSubscribed(payload);
  }
}

function onHashOrRouteChange() {
  syncMapaFaseRoute(currentPathFromWindow());
}

function bindHashListener() {
  if (hashListenerBound || typeof window === 'undefined') return;
  hashListenerBound = true;
  window.addEventListener('hashchange', onHashOrRouteChange);
}

function bindConfigListener() {
  const store = window.store;
  if (!store || store.__mapaFaseConfigListener) return;

  store.__mapaFaseConfigListener = true;
  store.subscribe(() => {
    const cfg = getClientConfigFromStore();
    const hasMapa = !!(cfg && cfg.mapa_fase_socket);
    if (hasMapa && !lastConfigHadMapaSocket && isMapaPath(currentPathFromWindow())) {
      syncMapaFaseRoute(currentPathFromWindow(), cfg);
    }
    lastConfigHadMapaSocket = hasMapa;
  });
}

/** Registra handlers uma única vez — sobrevive a navegação entre menus. */
export function wireMapaFaseSocketBridge() {
  if (wired) return;
  wired = true;

  socketService.addEventListener('mapa_fase_update', handleUpdate);
  socketService.addEventListener('mapa_fase_subscribed', handleSubscribed);
  bindHashListener();
  bindConfigListener();

  const cfg = getClientConfigFromStore();
  lastConfigHadMapaSocket = !!(cfg && cfg.mapa_fase_socket);
}

export function isMapaFaseLiveAtivo() {
  return mapaFaseLiveAtivo;
}

export function getGruposMapaFaseAtivos() {
  return gruposAtivos;
}

export function setMapaFaseLiveAtivo(ativo, grupos) {
  mapaFaseLiveAtivo = !!ativo;
  if (!mapaFaseLiveAtivo) {
    stopMapaRouteKeeper();
    gruposAtivos = [];
    socketService.unsubscribeMapaFaseTodos();
    return;
  }
  if (grupos !== undefined) {
    gruposAtivos = (Array.isArray(grupos) ? grupos : [grupos])
      .map(g => parseInt(g, 10))
      .filter(g => g > 0);
  }
}

export function deactivateMapaFaseLive() {
  setMapaFaseLiveAtivo(false);
}

export function syncMapaFaseRoute(pathname, config) {
  wireMapaFaseSocketBridge();

  const cfg = config || getClientConfigFromStore();
  const path = normalizePath(pathname || currentPathFromWindow());

  if (!cfg || !cfg.mapa_fase_socket) {
    stopMapaRouteKeeper();
    return;
  }

  if (!isMapaPath(path)) {
    stopMapaRouteKeeper();
    setMapaFaseLiveAtivo(false);
    return;
  }

  if (mapaFaseLiveAtivo) {
    ensureMapaLiveKeeper();
  } else {
    stopMapaRouteKeeper();
  }
}

/** Ativa leitura ao vivo (socket + keeper) para os grupos informados. */
export function activateMapaFaseLive(grupos) {
  setMapaFaseLiveAtivo(true, grupos);
  if (isMapaPath(currentPathFromWindow())) {
    ensureMapaLiveKeeper();
  }
}

export function stopMapaFaseRouteSync() {
  stopMapaRouteKeeper();
  deactivateMapaFaseLive();
}
